import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { EchoInfoFieldIds, EchoSectionIds } from './base-config-models';
import { MonitorSeriesNames } from '../../../location-card.constants';
import { openSiltDialog } from '../../actions';
import { field } from '../../core/field-schema';
import { required, pattern } from '../../core/validator-builders';
import { ComportOption, SampleRateElement } from '../../../models/location-card.model';
import { EchoLocationCardState } from './models';

/**
 * Echo Info Configuration Section
 */
export const echoInfo = (state: EchoLocationCardState): Section => {
    const details = state.locationDetails && state.locationDetails.length > 0 ? state.locationDetails[0] : null;
    const settings = state.settings;
    const config = state.monitorConfiguration;

    const isSwitchMonitor = details && details.series !== MonitorSeriesNames.echo;
    const isEditMode = !!details;

    const disableFieldClass = !state.permissions.isMonitorEditor ? 'echo-section disable-field' : 'echo-section';

    return {
        info: {
            id: EchoSectionIds.EchoInfo,
            title: 'ADD_EDIT.ECHO',
            displayTitle: true,
            expandable: true,
            expanded: false,
            panelClass: disableFieldClass,
        },
        rows: [
            // Subtitle row
            {
                info: {
                    id: 'subtitle-row',
                },
                items: [
                    {
                        id: 'echo-subtitle',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        label: 'ADD_EDIT.ADD_EDIT_SUBTITLE',
                        panelClass: 'input-text subtitle-text',
                    },
                ],
            },

            // Store Temp and Battery Voltage row
            {
                info: {
                    id: 'store-temp-battery-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(EchoInfoFieldIds.StoreTemperature, 'storetemperature', {
                        label: 'ADD_EDIT.STORE_MONITOR_TEMPERATURE',
                        panelClass: 'each-Item item-flex-center',
                        value: config ? config.storetemperature : false,
                    }),
                    field.select<SampleRateElement, number>(EchoInfoFieldIds.BatteryVoltage, 'storebattery', {
                        placeholder: 'ADD_EDIT.STORE_BATTERY_VOLTAGE',
                        value: config?.storebattery ?? 1,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.BatteryVoltageOptions,
                        panelClass: 'each-Item',
                    }),
                ],
            },

            // Sensor row
            {
                info: {
                    id: 'sensor-row',
                    panelClass: 'each-Item',
                },
                items: [
                    field.select<ComportOption, string>(EchoInfoFieldIds.Sensor, 'sensor', {
                        placeholder: 'ADD_EDIT.SENSOR',
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        panelClass: 'full-width',
                        value: 'ADD_EDIT.LONG_RANGE_DEPTH',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.EchoInfoSensorOptions,
                    }),
                ],
            },

            // Silt and HC Value row
            {
                info: {
                    id: 'silt-hc-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    // Silt field with edit button
                    {
                        id: 'silt-container',
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'silt-wrapper',
                                displayTitle: false,
                                title: '',
                                expandable: false,
                                panelClass: 'custom-flat-button custom-width slide-active-display',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'silt-input-row',
                                        panelClass: 'flex',
                                    },
                                    items: [
                                        field.number(EchoInfoFieldIds.Silt, 'silt', {
                                            panelClass: 'full-width',
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SILT',
                                            placeholderSuffix: settings.depthUnit,
                                            value: isEditMode && !isSwitchMonitor ? Number(config.silt.toFixed(2)) : 0,
                                            readonly: true,
                                            validators: [
                                                pattern({
                                                    value: settings.isMetric
                                                        ? REGEX_CONFIG.installationMetricValuePattern
                                                        : REGEX_CONFIG.installationValuePattern,
                                                }),
                                            ],
                                        }),
                                        {
                                            id: EchoInfoFieldIds.SiltEditButton,
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Button,
                                            visible: state.permissions.isBasicDataEditingAllowed,
                                            iconName: 'edit',
                                            tooltip: 'COMMON.SILT_EDITOR',
                                            disabled: !isEditMode,
                                            panelClass: 'no-margin',
                                            events: {
                                                onClick: () => [
                                                    openSiltDialog({ updateFieldId: EchoInfoFieldIds.Silt, mpIndex: 0 }),
                                                ],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },

                    // HC Value
                    field.number(EchoInfoFieldIds.HcValue, 'hcValue', {
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.HC_VALUE',
                        value: isEditMode && !isSwitchMonitor ? config.hc : 0,
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.hcValuePattern,
                                message: 'ADD_EDIT.HC_VALUE_ERROR_MSG',
                            }),
                        ],
                    }),
                ],
            },
        ],
    };
};
